import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "../../../../components/sprite/components/loader/index";
import Avatar from "../../../../components/avatar/index";
import { NavLink } from "react-router-dom";
import Icon from "../../../../components/icon/index";
import Button from '../../../../components/button';
import { toLocalDateTimeWithoutSeconds } from '../../../../utils/index';
import { sharingPageReady } from '../../../../../actions/profileActions';
import {
	fetchContactsHistoryLogs,
	fetchNextContactsHistoryLogs,
	loadNextContactsHistory as setLoadNextContactsHistory,
	revertContactHistory,
	restoreContactHistory
} from '../../../../../actions/contactsHistoryLogsActions';
import { isUpgradeToBusinessPopup as setIsUpgradeToBusinessPopup } from '../../../../../actions/popupActions';
import DiffViewer from './components/diff-viewer';
import InlineDiff from './components/inline-diff';
import RevertModal from './components/revert-modal';
import RestoreModal from './components/restore-modal';
import './index.css';

const SOURCE_LABELS = {
	WEBAPP: 'Webapp',
	IPAD_APP: 'Ipad App',
	IPHONE_APP: 'Iphone App',
	ANDROID_APP: 'Android App',
	GOOGLE_CONTACTS: 'Google Sync',
	MOBILE_APP: 'Mobile App',
	DOCS_ADDON: 'Docs Add-on',
	DRIVE_ADDON: 'Drive Add-on',
	GMAIL_ADDON: 'Gmail Add-on',
	CALENDAR_ADDON: 'Calendar Add-on',
	CHROME_EXTENSION: 'Google Contacts interface',
	THIRD_PARTY_APP: 'Third-party App',
	THIRD_PARTY_ZAPIER: 'Zapier',
	THIRD_PARTY_UNKNOWN: 'Third-party App',
	UNKNOWN: null,
};

const SOURCE_ICONS = {
	WEBAPP: 'desktop',
	IPAD_APP: 'tablet',
	IPHONE_APP: 'apple',
	ANDROID_APP: 'android',
	GOOGLE_CONTACTS: 'google',
	MOBILE_APP: 'mobile',
	DOCS_ADDON: 'file-text',
	DRIVE_ADDON: 'hdd-o',
	GMAIL_ADDON: 'envelope',
	CALENDAR_ADDON: 'calendar',
	CHROME_EXTENSION: 'chrome',
};

const FEATURE_LABELS = {
	'From Application Interface': 'Contact Form',
	'From Recording': 'Contact Form',
	'Created Using Directory Feature': 'Directory Feature',
	'DETECTED_FROM_EMAIL': 'Detected from Email',
	'DETECTED_FROM_SIGNATURE': 'Signature Extractor'
};


const resolveLabelName = (groupId, itemGroupNames = {}, reduxLabels = []) => {
	if (!groupId) return '';
	const cleanId = String(groupId).includes(':') ? String(groupId).split(':')[0] : String(groupId);
	if (cleanId === '6') return 'My Contacts';
	if (cleanId === 'starred') return 'Starred in Android';
	if (itemGroupNames && itemGroupNames[cleanId]) return itemGroupNames[cleanId];
	if (itemGroupNames && itemGroupNames[groupId]) return itemGroupNames[groupId];
	const found = reduxLabels.find(l => l.groupId === cleanId || l.id === cleanId || l.groupId === groupId);
	if (found && (found.name || found.title)) return found.name || found.title;
	return cleanId;
};

const getEventNarrative = (item, reduxLabels = []) => {
	const author = item.actionBy?.name || item.actionBy?.email || 'Someone';
	const entry = item.currentContactEntry || item.snapshot || {};
	const contactName = [entry.givenName, entry.middleName, entry.familyName].filter(Boolean).join(' ') || entry.displayName || entry.email || 'Contact';

	// Determine label names
	const rawGroups = item.groups || entry.groups || [];
	const resolvedLabels = Array.from(new Set(
		rawGroups
			.map(g => resolveLabelName(g, item.groupNames, reduxLabels))
			.filter(name => name && name !== 'My Contacts')
	));

	const primaryLabel = resolvedLabels.length > 0 ? resolvedLabels[0] : (rawGroups.some(g => String(g).startsWith('6')) ? 'My Contacts' : null);

	if (item.changeType === 'CONTACT_CREATED') {
		return {
			author,
			action: 'created contact',
			contactName,
			context: primaryLabel ? `in label "${primaryLabel}"` : ''
		};
	}

	if (item.changeType === 'CONTACT_MOVED') {
		let oldLabel = null;
		let newLabel = null;
		if (item.structuredChanges) {
			const moveChange = item.structuredChanges.find(c => c.fieldName === 'group');
			if (moveChange) {
				oldLabel = resolveLabelName(moveChange.oldValue, item.groupNames, reduxLabels);
				newLabel = resolveLabelName(moveChange.newValue, item.groupNames, reduxLabels);
			}
		}
		if (!oldLabel && !newLabel && item.changes?.group) {
			oldLabel = resolveLabelName(item.changes.group.oldValue, item.groupNames, reduxLabels);
			newLabel = resolveLabelName(item.changes.group.newValue, item.groupNames, reduxLabels);
		}

		if (oldLabel && newLabel) {
			return {
				author,
				action: 'moved contact',
				contactName,
				context: `from "${oldLabel}" to "${newLabel}"`
			};
		} else if (newLabel) {
			return {
				author,
				action: 'added contact',
				contactName,
				context: `to label "${newLabel}"`
			};
		} else if (oldLabel) {
			return {
				author,
				action: 'removed contact',
				contactName,
				context: `from label "${oldLabel}"`
			};
		}
		return {
			author,
			action: 'moved contact',
			contactName,
			context: 'between labels'
		};
	}

	if (item.actionType === 'REVERT' || item.changeType === 'REVERT') {
		const validChanges = (item.structuredChanges || []).filter(c => {
			const o = c.oldValue != null ? String(c.oldValue).trim() : '';
			const n = c.newValue != null ? String(c.newValue).trim() : '';
			return o !== n;
		});
		const legacyKeys = item.changes ? Object.keys(item.changes) : [];
		const count = item.structuredChanges ? validChanges.length : legacyKeys.length;
		const badge = count > 0 ? `${count} ${count > 1 ? 'fields' : 'field'} reverted` : '';
		return {
			author,
			action: 'reverted changes on',
			contactName,
			context: '',
			badge
		};
	}

	if (item.changeType === 'CONTACT_UPDATED') {
		const validChanges = (item.structuredChanges || []).filter(c => {
			const o = c.oldValue != null ? String(c.oldValue).trim() : '';
			const n = c.newValue != null ? String(c.newValue).trim() : '';
			return o !== n;
		});
		const legacyKeys = item.changes ? Object.keys(item.changes) : [];
		const count = item.structuredChanges ? validChanges.length : legacyKeys.length;
		const badge = count > 0 ? `${count} ${count > 1 ? 'fields' : 'field'} updated` : '';
		return {
			author,
			action: 'updated contact',
			contactName,
			context: '',
			badge
		};
	}

	if (item.changeType === 'CONTACT_DELETED') {
		return {
			author,
			action: 'deleted contact',
			contactName,
			context: primaryLabel ? `from label "${primaryLabel}"` : ''
		};
	}

	if (item.changeType === 'RESTORE' || item.changeType === 'CONTACT_RESTORED' || item.actionType === 'RESTORE') {
		return {
			author,
			action: 'restored contact',
			contactName,
			context: primaryLabel ? `to label "${primaryLabel}"` : ''
		};
	}

	return {
		author,
		action: 'modified contact',
		contactName,
		context: ''
	};
};


function ContactsHistoryLogs() {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const contactsHistoryLogsRef = useRef(null);

	// Redux State
	const contactsHistoryLogs = useSelector((state) => state.contactsHistoryLogsReducer.contactsHistoryLogs);
	const loadNextContactsHistory = useSelector(state => state.contactsHistoryLogsReducer.loadNextContactsHistory);
	const selectedContactsGroup = useSelector(state => state.contactsGroupItemReducer.getSelectedContactsGroup);
	const labels = useSelector(state => state.contactsGroupItemReducer.findLabels?.contactsGroups || []);
	const profile = useSelector(state => state.profileReducer.getProfile);

	// Plan & Permissions
	const plan = profile?.domain?.plan || '';
	const isSuperUser = Boolean(profile?.account?.superUser || profile?.account?.isAdmin);
	const isTeamPlan = !isSuperUser && plan === 'TEAM';
	const isFreemiumPlan = !isSuperUser && (plan === 'FREEMIUM' || plan === 'FREE_EDITION');
	const isBusinessOrHigher = isSuperUser || ['BUSINESS', 'ENTERPRISE', 'BUSINESS_TRIAL', 'TRIAL'].includes(plan);

	// Local State: Filters & Search
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const [selectedAction, setSelectedAction] = useState('');
	const [datePreset, setDatePreset] = useState('ALL');
	const [customStartDate, setCustomStartDate] = useState('');
	const [customEndDate, setCustomEndDate] = useState('');
	const [selectedGroupId, setSelectedGroupId] = useState('');

	// Local State: UI interactions
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [expandedRowIds, setExpandedRowIds] = useState({});
	const [revertTargetItem, setRevertTargetItem] = useState(null);
	const [restoreTargetItem, setRestoreTargetItem] = useState(null);
	const [isActionLoading, setIsActionLoading] = useState(false);
	const [actionError, setActionError] = useState(null);
	const [toastMessage, setToastMessage] = useState(null);

	// Debounce search query
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedQuery(searchQuery);
		}, 350);
		return () => clearTimeout(timer);
	}, [searchQuery]);

	// Auto-clear toast
	useEffect(() => {
		if (toastMessage) {
			const timer = setTimeout(() => setToastMessage(null), 5000);
			return () => clearTimeout(timer);
		}
	}, [toastMessage]);

	// Initialize sharing page ready
	useEffect(() => {
		dispatch(sharingPageReady(true));
	}, [dispatch]);

	// Fetch logs on filter changes
	useEffect(() => {
		if (isTeamPlan) {
			// Team plan: always fetch default unfilterable list
			dispatch(fetchContactsHistoryLogs());
			return;
		}

		let startDateMs = null;
		let endDateMs = null;
		const now = Date.now();

		if (datePreset === '7D') {
			startDateMs = now - 7 * 24 * 60 * 60 * 1000;
		} else if (datePreset === '30D') {
			startDateMs = now - 30 * 24 * 60 * 60 * 1000;
		} else if (datePreset === '90D') {
			startDateMs = now - 90 * 24 * 60 * 60 * 1000;
		} else if (datePreset === 'CUSTOM') {
			if (customStartDate) startDateMs = new Date(customStartDate).getTime();
			if (customEndDate) endDateMs = new Date(customEndDate).getTime() + 86399999;
		}

		const filters = {
			page: 1,
			query: debouncedQuery.trim() || undefined,
			action: selectedAction || undefined,
			groupId: selectedGroupId || undefined,
			startDate: startDateMs || undefined,
			endDate: endDateMs || undefined
		};

		dispatch(fetchContactsHistoryLogs(filters));
	}, [dispatch, debouncedQuery, selectedAction, selectedGroupId, datePreset, customStartDate, customEndDate, isTeamPlan]);

	// Infinite scroll
	useEffect(() => {
		if (loadNextContactsHistory) {
			const nextPage = contactsHistoryLogs?.pageInfo?.next || ((contactsHistoryLogs?.pageInfo?.page || 1) + 1);
			dispatch(fetchNextContactsHistoryLogs(nextPage));
		}
	}, [dispatch, loadNextContactsHistory, contactsHistoryLogs]);

	const handleScroll = () => {
		if (isTeamPlan) return; // Team plan cannot paginate beyond first page

		if (contactsHistoryLogsRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = contactsHistoryLogsRef.current;
			if (scrollTop + clientHeight >= scrollHeight - 200) {
				const hasMore = Boolean(contactsHistoryLogs?.pageInfo?.hasMore || (contactsHistoryLogs?.pageInfo?.next != null));
				if (!loadNextContactsHistory && hasMore) {
					dispatch(setLoadNextContactsHistory(true));
				}
			}
		}
	};

	const handleRefresh = () => {
		if (contactsHistoryLogsRef.current) {
			contactsHistoryLogsRef.current.scrollTop = 0;
		}
		dispatch(setLoadNextContactsHistory(false));
		setIsRefreshing(true);

		const filters = isTeamPlan ? {} : {
			page: 1,
			query: debouncedQuery.trim() || undefined,
			action: selectedAction || undefined,
			groupId: selectedGroupId || undefined
		};

		dispatch(fetchContactsHistoryLogs(filters));
		setTimeout(() => setIsRefreshing(false), 800);
	};

	const handleLockedClick = (e) => {
		if (isTeamPlan) {
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			dispatch(setIsUpgradeToBusinessPopup(true));
			return true;
		}
		return false;
	};

	const resetFilters = () => {
		setSearchQuery('');
		setSelectedAction('');
		setDatePreset('ALL');
		setCustomStartDate('');
		setCustomEndDate('');
		setSelectedGroupId('');
	};

	const hasActiveFilters = Boolean(searchQuery || selectedAction || selectedGroupId || datePreset !== 'ALL');

	const toggleRowExpand = (id, e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setExpandedRowIds(prev => ({
			...prev,
			[id]: !prev[id]
		}));
	};

	// Revert action handler
	const handleRevertConfirm = async (historyId) => {
		setIsActionLoading(true);
		setActionError(null);
		try {
			await dispatch(revertContactHistory(historyId));
			setRevertTargetItem(null);
			setToastMessage({ type: 'success', text: 'Contact changes successfully reverted!' });
			handleRefresh();
		} catch (err) {
			setActionError(err);
		} finally {
			setIsActionLoading(false);
		}
	};

	// Restore action handler
	const handleRestoreConfirm = async (historyId, targetGroupId) => {
		setIsActionLoading(true);
		setActionError(null);
		try {
			await dispatch(restoreContactHistory(historyId, targetGroupId));
			setRestoreTargetItem(null);
			setToastMessage({ type: 'success', text: 'Contact successfully restored!' });
			handleRefresh();
		} catch (err) {
			setActionError(err);
		} finally {
			setIsActionLoading(false);
		}
	};

	const getActionBadge = (changeType, actionType) => {
		if (actionType === 'REVERT' || changeType === 'REVERT') {
			return <span className="badge badge-warning action-badge">Reverted</span>;
		}
		if (changeType === 'CONTACT_RESTORED' || changeType === 'RESTORE' || actionType === 'RESTORE') {
			return <span className="badge badge-success action-badge">Restored</span>;
		}
		switch (changeType) {
			case 'CONTACT_CREATED':
				return <span className="badge badge-success action-badge">Created</span>;
			case 'CONTACT_DELETED':
				return <span className="badge badge-danger action-badge">Deleted</span>;
			case 'CONTACT_MOVED':
				return <span className="badge badge-info action-badge">Moved</span>;
			case 'CONTACT_UPDATED':
			default:
				return <span className="badge badge-primary action-badge">Updated</span>;
		}
	};

	return (
		<div className="contacts-history-logs">
			{/* Top Header */}
			<div className="contacts-history-logs__header">
				<div className="d-flex align-items-center">
					<h2 className="m-0 font-weight-bold">{t('contactsHistoryLogsTitle')}</h2>
					{contactsHistoryLogs && contactsHistoryLogs.logs?.length > 0 && (
						<Button onClick={handleRefresh} className="btn-link pl-2 p-0 text-muted">
							<Icon animated={isRefreshing ? 'spin' : null} name="refresh" />
						</Button>
					)}
				</div>
				{selectedContactsGroup && (
					<div>
						<NavLink
							to={`/label/${selectedContactsGroup.id === '6' ? selectedContactsGroup.id + '-' + selectedContactsGroup.ownerId : selectedContactsGroup.id}`}
							className="btn-link pr-2"
						>
							<Icon name="long-arrow-left" className="mr-2" /> {t("getBackText")}
						</NavLink>
					</div>
				)}
			</div>

			{/* Freemium 30-Day Notice */}
			{isFreemiumPlan && (
				<div className="history-plan-banner history-banner-freemium mx-3 mb-2 p-2 px-3 rounded d-flex align-items-center justify-content-between">
					<div className="d-flex align-items-center">
						<Icon name="info-circle" className="text-primary mr-2 fa-lg" />
						<small>
							Showing changes from the <strong>past 30 days</strong>. Upgrade to Business for unlimited history, full audit trails, and 1-click restore/revert.
						</small>
					</div>
					<Button className="btn-primary btn-sm ml-3" onClick={() => dispatch(setIsUpgradeToBusinessPopup(true))}>
						Upgrade
					</Button>
				</div>
			)}

			{/* Success Toast */}
			{toastMessage && (
				<div className={`alert alert-${toastMessage.type} mx-3 mb-2 py-2 px-3 d-flex align-items-center justify-content-between`}>
					<span><Icon name="check-circle" className="mr-2" /> {toastMessage.text}</span>
					<button type="button" className="close" onClick={() => setToastMessage(null)}>&times;</button>
				</div>
			)}

			{/* Filter & Search Bar */}
			<div className={`history-filter-bar px-3 py-2 ${isTeamPlan ? 'locked-filter-bar' : ''}`}>
				<div className="row g-2 align-items-center">
					{/* Search Input */}
					<div className="col-12 col-md-3 position-relative mb-2 mb-md-0">
						<div className="input-group input-group-sm" onClick={isTeamPlan ? handleLockedClick : null}>
							<div className="input-group-prepend">
								<span className="input-group-text bg-white">
									<Icon name="search" className="text-muted" />
								</span>
							</div>
							<input
								type="text"
								className="form-control"
								placeholder="Search contact..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								disabled={isTeamPlan}
							/>
							{isTeamPlan && (
								<div className="input-group-append">
									<span className="input-group-text bg-light text-warning" title="Upgrade to Business to search history">
										<Icon name="lock" />
									</span>
								</div>
							)}
						</div>
					</div>

					{/* Action Dropdown */}
					<div className="col-6 col-md-2 mb-2 mb-md-0">
						<div className="position-relative" onClick={isTeamPlan ? handleLockedClick : null}>
							<select
								className="form-control form-control-sm"
								value={selectedAction}
								onChange={(e) => setSelectedAction(e.target.value)}
								disabled={isTeamPlan}
							>
								<option value="">All Actions</option>
								<option value="CONTACT_CREATED">Created</option>
								<option value="CONTACT_UPDATED">Updated</option>
								<option value="CONTACT_DELETED">Deleted</option>
								<option value="CONTACT_MOVED">Moved</option>
								<option value="RESTORE">Restored</option>
								<option value="REVERT">Reverted</option>
							</select>
							{isTeamPlan && <span className="locked-badge"><Icon name="lock" /></span>}
						</div>
					</div>

					{/* Date Range Selector */}
					<div className="col-6 col-md-2 mb-2 mb-md-0">
						<div className="position-relative" onClick={isTeamPlan ? handleLockedClick : null}>
							<select
								className="form-control form-control-sm"
								value={datePreset}
								onChange={(e) => setDatePreset(e.target.value)}
								disabled={isTeamPlan}
							>
								<option value="ALL">All Time</option>
								<option value="7D">Last 7 Days</option>
								<option value="30D">Last 30 Days</option>
								<option value="90D">Last 90 Days</option>
								<option value="CUSTOM">Custom Range...</option>
							</select>
							{isTeamPlan && <span className="locked-badge"><Icon name="lock" /></span>}
						</div>
					</div>

					{/* Label / Group Selector */}
					<div className="col-6 col-md-2 mb-2 mb-md-0">
						<div className="position-relative" onClick={isTeamPlan ? handleLockedClick : null}>
							<select
								className="form-control form-control-sm"
								value={selectedGroupId}
								onChange={(e) => setSelectedGroupId(e.target.value)}
								disabled={isTeamPlan}
							>
								<option value="">All Labels</option>
								{labels.map((grp) => (
									<option key={grp.id} value={grp.id}>
										{grp.name}
									</option>
								))}
							</select>
							{isTeamPlan && <span className="locked-badge"><Icon name="lock" /></span>}
						</div>
					</div>

					{/* Reset Filters / Upgrade Button */}
					<div className="col-6 col-md-3 text-right">
						{isTeamPlan ? (
							<Button
								className="btn-outline-primary btn-sm btn-block"
								onClick={() => dispatch(setIsUpgradeToBusinessPopup(true))}
							>
								<Icon name="lock" className="mr-1" /> Upgrade to Filter
							</Button>
						) : (
							hasActiveFilters && (
								<Button className="btn-link text-muted btn-sm" onClick={resetFilters}>
									<Icon name="times-circle" className="mr-1" /> Reset filters
								</Button>
							)
						)}
					</div>
				</div>

				{/* Custom Date Inputs if CUSTOM selected */}
				{!isTeamPlan && datePreset === 'CUSTOM' && (
					<div className="row mt-2 g-2 align-items-center">
						<div className="col-auto">
							<small className="text-muted font-weight-bold">From:</small>
						</div>
						<div className="col-auto">
							<input
								type="date"
								className="form-control form-control-sm"
								value={customStartDate}
								onChange={(e) => setCustomStartDate(e.target.value)}
							/>
						</div>
						<div className="col-auto">
							<small className="text-muted font-weight-bold">To:</small>
						</div>
						<div className="col-auto">
							<input
								type="date"
								className="form-control form-control-sm"
								value={customEndDate}
								onChange={(e) => setCustomEndDate(e.target.value)}
							/>
						</div>
					</div>
				)}
			</div>

			{/* Main Content Area */}
			<div className="contacts-history-logs_content" ref={contactsHistoryLogsRef} onScroll={handleScroll}>
				{contactsHistoryLogs?.pageInfo && contactsHistoryLogs.pageInfo.totalRecords !== undefined && (
					<div className="d-flex align-items-center justify-content-between px-3 py-2 bg-light border-bottom text-muted small" style={{ minHeight: '38px' }}>
						<span>
							<Icon name="database" className="mr-1 text-primary" />
							{contactsHistoryLogs.pageInfo.totalRecords > 0 ? (
								<>
									Showing <strong>{contactsHistoryLogs.logs?.length || 0}</strong> of <strong>{contactsHistoryLogs.pageInfo.totalRecords}</strong> events found
								</>
							) : (
								<>No events found for this query</>
							)}
						</span>
						{hasActiveFilters && (
							<button
								type="button"
								className="btn btn-link btn-sm p-0 text-muted"
								onClick={resetFilters}
							>
								Clear active query
							</button>
						)}
					</div>
				)}
				{contactsHistoryLogs !== null ? (
					contactsHistoryLogs.logs?.length > 0 ? (
						<>
							{contactsHistoryLogs.logs.map((item) => {
								const sourceLabel = item.source && SOURCE_LABELS[item.source];
								const sourceIcon = item.source && SOURCE_ICONS[item.source];
								const featureLabel = item.feature && FEATURE_LABELS[item.feature];
								const entry = item.currentContactEntry || item.snapshot || {};
								const authorName = item.actionBy?.name || item.actionBy?.email || 'Unknown';
								const isExpanded = Boolean(expandedRowIds[item.id]);

								const narrative = getEventNarrative(item, labels);

								// Collect resolved labels for contact
								const rawGroups = item.groups || entry.groups || [];
								const resolvedLabels = Array.from(new Set(
									rawGroups
										.map(g => resolveLabelName(g, item.groupNames, labels))
										.filter(name => name && name !== 'My Contacts')
								));

								const hasAi = Boolean(item.isAiContact || entry.aiContact || (entry.note && entry.note.includes('AI Note:')));
								const aiText = item.aiSummary || (entry.note && entry.note.includes('AI Note:') ? entry.note : null);

								// Primary email & phone
								const primaryEmail = entry.emails?.[0]?.value || entry.email;
								const primaryPhone = entry.phones?.[0]?.number || entry.phone;

								return (
									<div key={item.id} className="contacts-history-logs_content__row">
										<div className="d-flex align-items-start">
											{/* Contact Avatar */}
											<Avatar className="mr-3 mt-1" size={'3.5em'} src={item.picture || entry.photoUrl} />

											{/* Contact and Action Info */}
											<div className="flex-grow-1 min-width-0">
												{/* Badges bar */}
												<div className="d-flex align-items-center flex-wrap gap-2 mb-1">
													{getActionBadge(item.changeType, item.actionType)}

													{/* Source badge with icon */}
													{sourceLabel && (
														<small className="badge badge-light border text-dark font-weight-normal px-2 py-1">
															{sourceIcon && <Icon name={sourceIcon} className="mr-1 text-primary" />}
															{sourceLabel}
														</small>
													)}

													{/* Feature badge */}
													{featureLabel && (
														<small className="badge badge-light border text-muted px-2 py-1">
															{featureLabel}
														</small>
													)}

													{/* AI badge */}
													{hasAi && (
														<span className="badge badge-warning text-dark px-2 py-1 font-weight-bold">
															<Icon name="bolt" className="mr-1" /> AI Assistant
														</span>
													)}
												</div>

												{/* Storyline narrative headline */}
												<p className="lead m-0 text-dark history-storyline">
													<span className="font-weight-bold">{narrative.author}</span>
													{' '}
													<span className="text-secondary font-weight-normal">{narrative.action}</span>
													{' '}
													{item.contactId && item.changeType !== 'CONTACT_DELETED' ? (
														<NavLink to={`/contact/${item.contactId}`} className="text-primary font-weight-bold history-contact-link">
															{narrative.contactName}
														</NavLink>
													) : (
														<span className={`font-weight-bold ${item.changeType === 'CONTACT_DELETED' ? 'text-danger' : 'text-dark'}`}>
															{narrative.contactName}
														</span>
													)}
													{' '}
													{narrative.context && (
														<span className="text-muted font-weight-medium">{narrative.context}</span>
													)}
													{narrative.badge && (
														<span className="badge badge-light border text-muted ml-2 font-weight-normal align-middle" style={{ fontSize: '11px' }}>
															{narrative.badge}
														</span>
													)}
												</p>

												{/* SaaS-Grade Inline Differential for Updates */}
												{item.changeType === 'CONTACT_UPDATED' && (
													<InlineDiff
														item={item}
														groupNames={item.groupNames}
														reduxLabels={labels}
													/>
												)}

												{/* Contact Details summary: Job, Company, Email, Phone */}
												<div className="d-flex align-items-center flex-wrap text-muted small mt-1">
													{(entry.jobTitle || entry.company) && (
														<span className="mr-3">
															<Icon name="briefcase" className="mr-1 text-muted" />
															{entry.jobTitle && <span>{entry.jobTitle}</span>}
															{entry.jobTitle && entry.company && <span> at </span>}
															{entry.company && <strong>{entry.company}</strong>}
														</span>
													)}
													{primaryEmail && item.changeType !== 'CONTACT_UPDATED' && (
														<span className="mr-3">
															<Icon name="envelope-o" className="mr-1 text-muted" />
															{primaryEmail}
														</span>
													)}
													{primaryPhone && item.changeType !== 'CONTACT_UPDATED' && (
														<span className="mr-3">
															<Icon name="phone" className="mr-1 text-muted" />
															{primaryPhone}
														</span>
													)}
												</div>

												{/* Resolved Label Chips */}
												{resolvedLabels.length > 0 && (
													<div className="d-flex align-items-center flex-wrap mt-1">
														{resolvedLabels.map((lbl, idx) => (
															<span key={idx} className="badge badge-info badge-pill mr-1 mb-1 font-weight-normal">
																<Icon name="tag" className="mr-1" />
																{lbl}
															</span>
														))}
													</div>
												)}

												{/* AI Note Callout if present */}
												{aiText && (
													<div className="ai-note-callout mt-2 p-2 rounded">
														<div className="d-flex align-items-center text-info font-weight-bold mb-1 small">
															<Icon name="magic" className="mr-1" /> AI Assistant Note
														</div>
														<div className="small text-dark font-italic">
															{aiText}
														</div>
													</div>
												)}

												{/* Author & Timestamp */}
												<p className="m-0 text-muted small mt-2">
													By <span className="font-weight-bold text-dark">{authorName}</span>
													<span className="mx-1">•</span>
													{toLocalDateTimeWithoutSeconds(item.createdAt)}
												</p>
											</div>

											{/* Action Buttons (Restore / Revert / Diff toggle) */}
											<div className="history-row-actions d-flex align-items-center ml-2">
												{/* Revert Button */}
												{(item.changeType === 'CONTACT_UPDATED' || item.changeType === 'CONTACT_MOVED') && (
													<Button
														className={`btn-sm mr-2 ${isBusinessOrHigher ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
														onClick={isBusinessOrHigher ? () => setRevertTargetItem(item) : () => dispatch(setIsUpgradeToBusinessPopup(true))}
														title={isBusinessOrHigher ? "Revert this change" : "Upgrade to Business to revert"}
													>
														{isBusinessOrHigher ? (
															<span><Icon name="undo" className="mr-1" /> Revert</span>
														) : (
															<span><Icon name="lock" className="mr-1 text-warning" /> Revert</span>
														)}
													</Button>
												)}

												{/* Restore Button */}
												{item.changeType === 'CONTACT_DELETED' && (
													<Button
														className={`btn-sm mr-2 ${isBusinessOrHigher ? 'btn-outline-success' : 'btn-outline-secondary'}`}
														onClick={isBusinessOrHigher ? () => setRestoreTargetItem(item) : () => dispatch(setIsUpgradeToBusinessPopup(true))}
														title={isBusinessOrHigher ? "Restore deleted contact" : "Upgrade to Business to restore"}
													>
														{isBusinessOrHigher ? (
															<span><Icon name="recycle" className="mr-1" /> Restore</span>
														) : (
															<span><Icon name="lock" className="mr-1 text-warning" /> Restore</span>
														)}
													</Button>
												)}

												{/* Expand / Collapse Details Button */}
												<Button
													className="btn-link btn-sm text-secondary p-1"
													onClick={(e) => toggleRowExpand(item.id, e)}
													title={isExpanded ? "Hide Details" : "Show Details"}
												>
													<Icon name={isExpanded ? "chevron-up" : "chevron-down"} />
												</Button>
											</div>
										</div>

										{/* Inline Diff Viewer */}
										{isExpanded && (
											<div className="history-row-diff-container mt-2">
												<DiffViewer item={item} groupNames={item.groupNames} reduxLabels={labels} />
											</div>
										)}
									</div>
								);
							})}

							{loadNextContactsHistory && (
								<div className="text-center py-3 text-muted">
									<Icon animated="spin" name="spinner" className="mr-2" />
									Loading more events...
								</div>
							)}

							{/* Team Plan Limit Banner */}
							{isTeamPlan && contactsHistoryLogs.logs.length >= 20 && (
								<div className="history-team-limit-banner p-3 text-center bg-light border rounded mt-3">
									<Icon name="lock" className="text-warning mr-2 fa-lg" />
									<span className="text-muted">
										Viewing latest 20 changes (Team plan limit). Upgrade to Business for unlimited history, full audit trails, and advanced filtering.
									</span>
									<Button className="btn-primary btn-sm ml-3" onClick={() => dispatch(setIsUpgradeToBusinessPopup(true))}>
										Upgrade to Business
									</Button>
								</div>
							)}
						</>
					) : contactsHistoryLogs.message ? (
						<div className="text-center py-5 text-muted">
							<Icon name="info-circle" className="fa-2x mb-2" />
							<p><em>{contactsHistoryLogs.message}</em></p>
						</div>
					) : (
						<div className="text-center py-5 text-muted">
							<Icon name="history" className="fa-2x mb-2" />
							<p><em>No contact history logs found matching criteria</em></p>
							{hasActiveFilters && (
								<Button className="btn-link btn-sm" onClick={resetFilters}>
									Clear all filters
								</Button>
							)}
						</div>
					)
				) : (
					<Loader />
				)}
			</div>

			{/* Revert Confirmation Modal */}
			<RevertModal
				isOpen={Boolean(revertTargetItem)}
				historyItem={revertTargetItem}
				onClose={() => setRevertTargetItem(null)}
				onConfirm={handleRevertConfirm}
				isLoading={isActionLoading}
				error={actionError}
			/>

			{/* Restore Confirmation Modal */}
			<RestoreModal
				isOpen={Boolean(restoreTargetItem)}
				historyItem={restoreTargetItem}
				labels={labels}
				onClose={() => setRestoreTargetItem(null)}
				onConfirm={handleRestoreConfirm}
				isLoading={isActionLoading}
				error={actionError}
			/>
		</div>
	);
}

export default ContactsHistoryLogs;
